import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { Button, Modal } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import TextareaEmailsField from 'components/TextareaEmailsField';
import { getArrayOfEmails, isEmail } from 'utils';

import './AddUsersModal.styles.scss';

type AddUsersModalProps = {
  className?: string;
  title: string;
  submitText?: string;
  handleOnSubmit?: (emails: string[]) => Promise<void> | void;
};

const AddUsersModal: ForwardRefRenderFunction<
  OverlayRef,
  AddUsersModalProps
> = (props, ref) => {
  const { className, title, submitText, handleOnSubmit } = props;

  const { popServerError } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const { t } = useTranslation();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('add-users-modal', className);

  const onSubmit = useCallback(
    async (values: { emails: string[]; emailsUnsubmited: string }) => {
      const { emails, emailsUnsubmited } = values;

      const finalEmails = getArrayOfEmails(emails, emailsUnsubmited);

      try {
        await handleOnSubmit(finalEmails);

        modal.close();
      } catch (err) {
        popServerError(err);
      }
    },
    [modal, handleOnSubmit, popServerError],
  );

  return (
    <Modal ref={modalRef} className={classes} title={title}>
      <Form
        onSubmit={onSubmit}
        initialValues={{ emails: [] }}
        render={({
          handleSubmit,
          submitting,
          values: { emails, emailsUnsubmited },
        }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="emails"
              label={t('emailTextareaFieldLabel')}
              component={TextareaEmailsField}
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>

              <Button
                type="submit"
                disabled={
                  submitting || (!emails.length && !isEmail(emailsUnsubmited))
                }
              >
                {submitText || t('save')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(AddUsersModal);
